import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import CustomButton from "../../components/common/CustomButton";
import FigmaBigCalendar from "../common/Calendar";
import TimeRangePicker from "../common/TimePicker";
import { useVendorSuggestAlternateTime } from "../../custom-hooks/queries";
import { toast } from "react-toastify";
import { formatDate } from "../../utils/HelperMethod";

export default function SuggestAlternateTimeModal({
  isOpen,
  onClose,
  bookingId,
  onSuccess,
  refetch,

  // 🔑 NEW
  mode = "edit", // "edit" | "view"
  initialSuggestions = [], // [{ suggestedDate, suggestedTimeSlots }]
}) {
  const { mutate, isPending } = useVendorSuggestAlternateTime();

  const [selectedDates, setSelectedDates] = useState([]);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [activeDateKey, setActiveDateKey] = useState(null);

  useEffect(() => {
    if (mode !== "view") return;
    if (!Array.isArray(initialSuggestions) || initialSuggestions.length === 0)
      return;

    const dates = [];
    const slots = {};

    initialSuggestions.forEach((item) => {
      if (!item?.suggestedDate) return;
      if (!Array.isArray(item?.timeSlots)) return;

      const dateObj = new Date(item.suggestedDate);
      const dateKey = format(dateObj, "yyyy-MM-dd");

      dates.push(dateObj);

      slots[dateKey] = item.timeSlots
        .map((slot) => {
          if (typeof slot !== "string") return null;

          const parts = slot.split(" - ");
          if (parts.length !== 2) return null;

          return {
            start: parts[0].trim(),
            end: parts[1].trim(),
          };
        })
        .filter(Boolean);
    });

    if (dates.length === 0) return;

    setSelectedDates(dates);
    setSlotsByDate(slots);

    // 🔥 THIS WAS THE MISSING PIECE
    const firstKey = format(dates[0], "yyyy-MM-dd");
    setActiveDateKey(firstKey);
  }, [mode, initialSuggestions]);
  useEffect(() => {
    if (mode !== "view") return;
    if (!Array.isArray(initialSuggestions) || initialSuggestions.length === 0)
      return;

    const dates = [];
    const slots = {};

    initialSuggestions.forEach((item) => {
      if (!item?.suggestedDate) return;
      if (!Array.isArray(item?.timeSlots)) return;

      const dateObj = new Date(item.suggestedDate);
      const dateKey = format(dateObj, "yyyy-MM-dd");

      dates.push(dateObj);

      slots[dateKey] = item.timeSlots
        .map((slot) => {
          if (typeof slot !== "string") return null;

          const parts = slot.split(" - ");
          if (parts.length !== 2) return null;

          return {
            start: parts[0].trim(),
            end: parts[1].trim(),
          };
        })
        .filter(Boolean);
    });

    if (dates.length === 0) return;

    setSelectedDates(dates);
    setSlotsByDate(slots);

    // 🔥 THIS WAS THE MISSING PIECE
    const firstKey = format(dates[0], "yyyy-MM-dd");
    setActiveDateKey(firstKey);
  }, [mode, initialSuggestions]);

  if (!isOpen) {
    return null;
  }

  const getDateKey = (date) => format(date, "yyyy-MM-dd");

  /* ================= VIEW MODE INIT ================= */

  // ✅ start/end are Date objects — convert to just HH:mm
  const formatTime = (date) => {
    if (!(date instanceof Date)) return "";
    return format(date, "HH:mm");
  };

  /* ================= EDIT MODE ONLY ================= */

  const handleDateSelect = (dates) => {
    if (mode === "view") return;

    setSelectedDates(dates);

    const updatedSlots = { ...slotsByDate };

    dates.forEach((date) => {
      const key = getDateKey(date);
      if (!updatedSlots[key]) {
        updatedSlots[key] = [{ start: "", end: "" }];
      }
    });

    if (!activeDateKey && dates.length > 0) {
      setActiveDateKey(getDateKey(dates[0]));
    }
  };

  const addSlot = () => {
    if (mode === "view" || !activeDateKey) return;

    const slots = slotsByDate[activeDateKey] || [];

    if (slots.length >= 3) {
      toast.error("Maximum 3 time slots allowed per day");
      return;
    }

    setSlotsByDate({
      ...slotsByDate,
      [activeDateKey]: [...slots, { start: "", end: "" }],
    });
  };

  const updateSlot = (index, updatedSlot) => {
    if (mode === "view" || !activeDateKey) return;

    const updatedSlots = [...slotsByDate[activeDateKey]];
    updatedSlots[index] = updatedSlot;

    setSlotsByDate({
      ...slotsByDate,
      [activeDateKey]: updatedSlots,
    });
  };

  const removeSlot = (index) => {
    if (mode === "view" || !activeDateKey) return;

    const updatedSlots = slotsByDate[activeDateKey].filter(
      (_, i) => i !== index
    );

    setSlotsByDate({
      ...slotsByDate,
      [activeDateKey]: updatedSlots.length
        ? updatedSlots
        : [{ start: "", end: "" }],
    });
  };

  const handleSubmit = () => {
    if (!bookingId) {
      toast.error("Booking id missing");
      return;
    }

    const suggestions = [];

    for (const date of selectedDates) {
      const key = format(date, "yyyy-MM-dd"); // ✅ FIX
      const slots = slotsByDate[key];

      // skip if no slots or all empty
      if (!slots || slots.every((s) => !s.start && !s.end)) {
        continue;
      }

      // error if half-filled
      if (slots.some((s) => !s.start || !s.end)) {
        toast.error(`Please complete all time slots for ${key}`);
        return;
      }

      suggestions.push({
        date: key,
        timeSlots: slots.map(
          (s) => `${formatTime(s.start)} - ${formatTime(s.end)}`
        ),
      });
    }

    if (suggestions.length === 0) {
      toast.error("Please add at least one valid time slot");
      return;
    }

    mutate(
      { bookingId, suggestions },
      {
        onSuccess: () => {
          toast.success("Alternate times suggested successfully");
          refetch?.();
          onSuccess?.();
          onClose();
        },
        onError: () => {
          toast.error("Failed to suggest alternate time");
        },
      }
    );
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[20px] w-[900px] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">
          {mode === "view" ? "Suggested Time" : "Suggest Alternate Time"}
        </h2>

        <div className="flex gap-6 mt-6">
          {/* Calendar */}
          <FigmaBigCalendar
            selected={selectedDates}
            onSelect={handleDateSelect}
            disabled={mode === "view"}
          />

          <div className="flex-1 space-y-4">
            {/* Date tabs */}
            <div className="flex gap-2 flex-wrap">
              {selectedDates.map((date) => {
                const key = format(date, "yyyy-MM-dd");
                return (
                  <button
                    key={key}
                    onClick={() => setActiveDateKey(key)}
                    className={`px-3 py-1 rounded text-sm ${
                      activeDateKey === key
                        ? "bg-toucanTeal text-white"
                        : "bg-gray-200"
                    }`}
                  >
                    {formatDate(key)}
                  </button>
                );
              })}
            </div>

            {/* Slots */}
            {activeDateKey && (
              <div className="border rounded-lg p-4 bg-gray-50 flex flex-wrap gap-2">
                {(slotsByDate[activeDateKey] || []).map((slot, index) => (
                  <div key={index} className="flex items-center gap-3">
                    {mode === "view" ? (
                      <span
                        className="
      px-3 py-1
      rounded-full
      text-sm
      bg-gray-200
      text-gray-800
      border border-gray-300
      whitespace-nowrap
    "
                      >
                        {slot.start} - {slot.end}
                      </span>
                    ) : (
                      <TimeRangePicker
                        value={slot}
                        onChange={(val) => updateSlot(index, val)}
                      />
                    )}

                    {mode === "edit" &&
                      slotsByDate[activeDateKey].length > 1 && (
                        <button
                          type="button"
                          className="text-red-500 text-sm"
                          onClick={() => removeSlot(index)}
                        >
                          Remove
                        </button>
                      )}
                  </div>
                ))}

                {mode === "edit" && (
                  <button
                    type="button"
                    onClick={addSlot}
                    className="text-sm text-primary"
                  >
                    + Add another slot
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-8">
          <CustomButton variant="secondary" onClick={onClose}>
            Close
          </CustomButton>

          {mode === "edit" && (
            <CustomButton
              variant="primary"
              loading={isPending}
              onClick={handleSubmit}
            >
              Suggest Time
            </CustomButton>
          )}
        </div>
      </div>
    </div>
  );
}
